import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { createOrg } from '../service'
import { Dialog, Button, TextField, Flex, Text } from '@radix-ui/themes'
import { useTranslation } from 'react-i18next'
import { useAuth } from '~/features/auth/AuthProvider'
import { useFeatureEnabled } from '~/features/internal-analytics/useFeatureEnabled'
import { FieldWrapper } from '~/components/FormFieldWrapper'
import { useOrgContext } from '~/features/orgnisations/hooks/orgCtx'

interface CreateOrgDialogProps {
  children: React.ReactElement
}

const CreateOrgDialog = ({ children }: CreateOrgDialogProps) => {
  const { t } = useTranslation(['settings', 'common'])
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const { session } = useAuth()
  const canCreateOrg = useFeatureEnabled('org_create')
  const queryClient = useQueryClient()
  const { setCurrentOrg } = useOrgContext()

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    // Clear the form when dialog is closed
    if (!newOpen) {
      setName('')
    }
  }

  const { mutate, isPending } = useMutation({
    mutationFn: ({ name, ownerId }: { name: string; ownerId: string }) => createOrg(name, ownerId),
    onSuccess: (newOrg) => {
      setOpen(false)
      if (session?.user?.id) {
        queryClient.invalidateQueries({ queryKey: ['orgs', session.user.id] })
      }
      setCurrentOrg(newOrg)
    },
    onError: (error) => {
      console.error('Error creating organization', error)
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (name.trim() && session?.user?.id && canCreateOrg) {
      mutate({ name: name.trim(), ownerId: session.user.id })
    }
  }

  const isSubmitDisabled = !name.trim() || !canCreateOrg || isPending

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Trigger>{children}</Dialog.Trigger>
      <Dialog.Content>
        <Dialog.Title>{t('organization.create.title', { defaultValue: 'Create Organization' })}</Dialog.Title>
        <form onSubmit={handleSubmit}>
          <Flex direction="column" gap="4" mt="4">
            <FieldWrapper
              label={t('organization.create.nameLabel', { defaultValue: 'Organization Name' })}
              htmlFor="org-name"
            >
              <TextField.Root
                id="org-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('organization.create.namePlaceholder', { defaultValue: 'Enter organization name' })}
                disabled={isPending}
              />
            </FieldWrapper>

            {!canCreateOrg && (
              <Text size="2" color="red">
                {t('organization.create.noPermission', {
                  defaultValue: 'You do not have permission to create organizations'
                })}
              </Text>
            )}

            <Flex gap="3" mt="4" justify="end">
              <Dialog.Close>
                <Button variant="soft" color="gray" disabled={isPending}>
                  {t('cancel', { ns: 'common' })}
                </Button>
              </Dialog.Close>
              <Button type="submit" disabled={isSubmitDisabled} loading={isPending}>
                {t('organization.create.submit', { defaultValue: 'Create Organization' })}
              </Button>
            </Flex>
          </Flex>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  )
}

export { CreateOrgDialog }
